import {
  autoUpdate,
  flip,
  offset as fuiOffset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react-dom-interactions'
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { DropdownContextValue, DropdownProps } from './Dropdown.types'

const DropdownContext = createContext<DropdownContextValue>(
  {} as DropdownContextValue
)

export const useDropdownContext = () => useContext(DropdownContext)

export const Dropdown = (props: DropdownProps) => {
  const {
    open: originalOpen,
    handler: originalHandler,
    placement,
    dismiss,
    offset,
    children,
  } = props

  const [internalOpen, setInternalOpen] = useState(false)
  const open = originalOpen || internalOpen
  const handler = originalHandler || setInternalOpen
  const { x, y, reference, floating, strategy, refs, update, context } =
    useFloating({
      open,
      onOpenChange: handler,
      middleware: [fuiOffset(offset), flip(), shift()],
      placement,
    })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context),
    useRole(context),
    useDismiss(context, dismiss),
  ])

  useEffect(() => {
    if (refs.reference.current && refs.floating.current && open) {
      return autoUpdate(refs.reference.current, refs.floating.current, update)
    }
  }, [open, update, refs.reference, refs.floating])

  const valueContext = useMemo(
    (): DropdownContextValue => ({
      open,
      strategy,
      x,
      y,
      context,
      reference,
      floating,
      getReferenceProps,
      getFloatingProps,
    }),
    [
      open,
      strategy,
      x,
      y,
      context,
      reference,
      floating,
      getFloatingProps,
      getReferenceProps,
    ]
  )

  return (
    <DropdownContext.Provider value={valueContext}>
      {children}
    </DropdownContext.Provider>
  )
}

Dropdown.displayName = 'Dropdown'
